import React, { useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { ArrowRightIcon, PlusIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useEscaCopy } from '../../i18n/escaCopy';
import { track } from '../../utils/analytics';
const EASE = [0.23, 1, 0.32, 1] as const;
export function ExpertiseSection() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const [activeIdx, setActiveIdx] = useState(0);
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const active = c.expertise.categories[activeIdx];
  const onCta = (e: React.MouseEvent) => {
    e.preventDefault();
    track('expertise_cta_click');
    document.getElementById('discuss')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section id="expertise" className="bg-white py-20 lg:py-28" ref={ref} data-id="element-351" data-mp="1sbjfvugzru9z">
      <div className="max-w-[88rem] mx-auto px-5 sm:px-8" data-id="element-352" data-mp="1lgdjz5ygwbze">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.55,
        ease: EASE
      }} className="max-w-[46rem]" data-id="element-353" data-mp="1lespfgc5kt1j">
          <h2 className="esca-h2 text-ink text-balance" data-id="element-354" data-mp="1uartst08aub4">
            {c.expertise.title}
          </h2>
          <p className="esca-lead mt-6 text-muted max-w-[56ch]" data-id="element-355" data-mp="1q9pwihpvgsbs">
            {c.expertise.lead}
          </p>
        </motion.div>

        {/* Desktop: category list + roles */}
        <div className="hidden lg:grid grid-cols-[minmax(0,22rem)_1fr] gap-16 mt-16 border-t border-line pt-12" data-id="element-356" data-mp="xkizjn62gasn">
          <div role="tablist" aria-orientation="vertical" className="flex flex-col" data-id="element-357" data-mp="1bg34tfsvguhl">
            {c.expertise.categories.map((cat, i) => {
            const isActive = i === activeIdx;
            return <button key={cat.name} role="tab" aria-selected={isActive} onClick={() => setActiveIdx(i)} className={`group relative text-left py-4 pl-6 pr-4 border-b border-line transition-colors duration-200 ${isActive ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-358" data-mp="2hyjg8pfhgnq4" data-mpi={i}>
                  <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] transition-[height,background-color] duration-200 ${isActive ? 'h-7 bg-accent' : 'h-0 bg-transparent group-hover:h-4 group-hover:bg-line'}`} data-id="element-359" data-mp="1tlek3hbb1881" />
                  <span className={`font-display text-lg tracking-[-0.02em] ${isActive ? 'font-semibold' : 'font-medium'}`} data-id="element-360" data-mp="15t7jg5yjqf2t">
                    {cat.name}
                  </span>
                </button>;
          })}
          </div>

          <div data-id="element-361" data-mp="alylnjg7b22c">
            <AnimatePresence mode="wait" data-id="element-362" data-mp="amgoiyxdcej8">
              <motion.ul key={active.name} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.22,
              ease: EASE
            }} className="grid grid-cols-2 gap-x-10" data-id="element-363" data-mp="ak48irb68e8t">
                {active.roles.map(role => <li key={role} className="py-3.5 text-[1.0625rem] text-ink border-b border-line" data-id="element-364" data-mp="3ntibq21nax9r">
                    {role}
                  </li>)}
              </motion.ul>
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile: accordion */}
        <div className="lg:hidden mt-12 border-t border-line" data-id="element-365" data-mp="35292ltvseaip">
          {c.expertise.categories.map((cat, i) => {
          const isOpen = openIdx === i;
          return <div key={cat.name} className="border-b border-line" data-id="element-366" data-mp="3058vrgr453qj" data-mpi={i}>
                <button type="button" onClick={() => setOpenIdx(isOpen ? null : i)} aria-expanded={isOpen} className="w-full flex items-center justify-between gap-4 py-5 text-left" data-id="element-367" data-mp="141avmulhhnvq">
                  <span className="font-display text-lg font-semibold text-ink tracking-[-0.02em]" data-id="element-368" data-mp="z8hs2a1r4ll8">
                    {cat.name}
                  </span>
                  <PlusIcon className={`flex-shrink-0 w-5 h-5 text-accent transition-transform duration-200 ease-out ${isOpen ? 'rotate-45' : ''}`} data-id="element-369" data-mp="ke4woz4qjwfb" />
                </button>
                <AnimatePresence initial={false} data-id="element-370" data-mp="gbgjkf8zo3cj">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: EASE
              }} className="overflow-hidden" data-id="element-371" data-mp="25otr0dcee7hv">
                      <ul className="pb-5" data-id="element-372" data-mp="2fv4tqqkplqse">
                        {cat.roles.map(role => <li key={role} className="py-2 text-[1rem] text-muted" data-id="element-373" data-mp="2tlzvzk0pd9n0">
                            {role}
                          </li>)}
                      </ul>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>

        {/* Foot CTA */}
        <div className="mt-16 lg:mt-20 grid lg:grid-cols-[1fr_auto] gap-8 lg:gap-16 items-end border-t border-line pt-10" data-id="element-374" data-mp="38hfwa6rvm1ru">
          <div className="max-w-[46ch]" data-id="element-375" data-mp="1i6b88qfmk6l5">
            <h3 className="font-display text-xl lg:text-2xl font-semibold text-ink tracking-[-0.02em]" data-id="element-376" data-mp="1i7culr23cfi6">
              {c.expertise.footTitle}
            </h3>
            <p className="mt-3 text-[1.0625rem] text-muted leading-relaxed" data-id="element-377" data-mp="3d9zs57h0v5td">
              {c.expertise.footText}
            </p>
          </div>
          <a href="#discuss" onClick={onCta} className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 text-base font-semibold text-white bg-accent hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap" data-id="element-378" data-mp="3n6vsgpwrolmu">
            {c.expertise.footCta}
            <ArrowRightIcon className="w-4 h-4 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-379" data-mp="50r78maxgwxu" />
          </a>
        </div>
      </div>
    </section>;
}